import React from 'react';

import Table from '../../Shared/UI/Table/components/Table';
import Thead from '../../Shared/UI/Table/components/Thead';
import Tbody from '../../Shared/UI/Table/components/Tbody';
import Tr from '../../Shared/UI/Table/components/Tr';
import Th from '../../Shared/UI/Table/components/Th';
import Td from '../../Shared/UI/Table/components/Td';
import Button from '../../Shared/UI/Button/components/Button';
import ActionRow from '../../Shared/UI/ActionRow/components/ActionRow';
import Accordion from '../../Shared/UI/Accordion/components/Accordion';
import Checkbox from '../../Shared/UI/Checkbox/components/Checkbox';
import Link from '../../Shared/UI/Link/components/Link';

import { useAllJobProspectsState, useAllJobProspectsActions } from './AllJobProspectsContext';
import { approveJobProspect, rejectJobProspect, superAdminApproveJobProspect, superAdminRejectJobProspect, superAdminBulkUpdateJobProspects, archiveJobProspect, bulkArchiveJobProspects } from './restApi';

const statusLabels = {
  awaiting_super_admin_approval: 'Awaiting Super Admin Approval',
  rejected_by_super_admin: 'Rejected By Super Admin',
  awaiting_admin_approval: 'Awaiting Admin Approval',
  rejected_by_admin: 'Rejected By Admin',
  not_started: 'Not Started',
  application_in_process: 'Application In Process',
  applied_and_waiting: 'Applied And Waiting',
  interviewing: 'Interviewing',
  offer_received: 'Offer Received',
  offer_accepted: 'Offer Accepted',
  not_hiring: 'Not Hiring',
  no_offer: 'No Offer',
  decided_against: 'Decided Against',
  waiting_for_decision: 'Waiting For Decision',
  closed: 'Closed',
};

const AllJobProspectsTable = () => {
  const state = useAllJobProspectsState();
  const { onResponse, onError, openDeleteJobProspectModal, openJobClosedWarningModal, toggleJobProspectSelection, selectAllJobProspects, clearSelection } = useAllJobProspectsActions();
  const authenticityToken = document.querySelector("meta[name='csrf-token']")?.getAttribute("content");
  const selectedJobProspectIds = state?.selectedJobProspectIds || [];

  const handleApproveJobProspect = (jobProspect) => {
    approveJobProspect({
      onResponse,
      onError,
      body: {
        authenticityToken,
        jobProspectId: jobProspect.id,
      },
    });
  };

  const handleRejectJobProspect = (jobProspect) => {
    rejectJobProspect({
      onResponse,
      onError,
      body: {
        authenticityToken,
        jobProspectId: jobProspect.id,
      },
    });
  };

  const handleSuperAdminApproveJobProspect = (jobProspect) => {
    superAdminApproveJobProspect({
      onResponse,
      onError,
      body: {
        authenticityToken,
        jobProspectId: jobProspect.id,
      },
    });
  };

  const handleSuperAdminRejectJobProspect = (jobProspect) => {
    superAdminRejectJobProspect({
      onResponse,
      onError,
      body: {
        authenticityToken,
        jobProspectId: jobProspect.id,
      },
    });
  };

  const handleSuperAdminBulkAction = (action) => {
    superAdminBulkUpdateJobProspects({
      onResponse,
      onError,
      body: {
        authenticityToken,
        jobProspectIds: selectedJobProspectIds,
        bulkAction: action,
      },
    });
  };

  const archivableStatuses = ['rejected_by_admin', 'rejected_by_super_admin', 'closed', 'no_offer', 'decided_against', 'not_hiring'];

  const handleArchiveJobProspect = (jobProspect) => {
    archiveJobProspect({
      onResponse,
      onError,
      body: {
        authenticityToken,
        jobProspectId: jobProspect.id,
      },
    });
  };

  const handleBulkArchiveJobProspects = () => {
    bulkArchiveJobProspects({
      onResponse,
      onError,
      body: {
        authenticityToken,
        jobProspectIds: selectedJobProspectIds,
      },
    });
  };

  const handleJobProspectLinkClick = (e, jobProspect) => {
    const isJobDescriptionClosed = jobProspect?.jobDescriptionClosedAt != null;

    if (isJobDescriptionClosed) {
      e.preventDefault();
      openJobClosedWarningModal(jobProspect);
    }
  };

  const renderStatusGroup = (jobProspectStatusGroup) => {
    const label = statusLabels[jobProspectStatusGroup?.status] || jobProspectStatusGroup?.status;
    const count = jobProspectStatusGroup.jobProspects.length;
    const status = jobProspectStatusGroup?.status;
    const isAwaitingSuperAdmin = status === 'awaiting_super_admin_approval';
    const isArchivable = archivableStatuses.includes(status);
    const showCheckboxes = isAwaitingSuperAdmin || isArchivable;

    const allIds = jobProspectStatusGroup.jobProspects.map(jp => jp.id);
    const allSelected = showCheckboxes && allIds.length > 0 && allIds.every(id => selectedJobProspectIds.includes(id));
    const someSelected = showCheckboxes && selectedJobProspectIds.length > 0;

    const handleSelectAll = () => {
      if (allSelected) {
        clearSelection();
      } else {
        selectAllJobProspects(allIds);
      }
    };

    return (
      <Accordion
        key={status}
        title={`${label} (${count})`}
      >
        {showCheckboxes && someSelected && (
          <ActionRow>
            <span>{selectedJobProspectIds.length} selected</span>
            {isArchivable ? (
              <Button onClick={() => handleBulkArchiveJobProspects()}>
                Archive Selected
              </Button>
            ) : (
              <>
                <Button onClick={() => handleSuperAdminBulkAction('approve')}>
                  Approve Selected
                </Button>
                <Button onClick={() => handleSuperAdminBulkAction('reject')}>
                  Reject Selected
                </Button>
              </>
            )}
          </ActionRow>
        )}
        <Table>
          <Thead>
            <Tr>
              {showCheckboxes && (
                <Th>
                  <Checkbox checked={allSelected} onChange={handleSelectAll} />
                </Th>
              )}
              <Th>Candidate</Th>
              <Th>Organization</Th>
              <Th>Job Description</Th>
              <Th>Board</Th>
              <Th>Added By</Th>
              <Th>Interest Level</Th>
              <Th />
            </Tr>
          </Thead>
          <Tbody>
            {jobProspectStatusGroup.jobProspects.map(jobProspect => {
              const jpStatus = jobProspect?.status;
              const isAwaitingApproval = jpStatus === 'awaiting_admin_approval';
              const isRejected = jpStatus === 'rejected_by_admin';
              const isAwaitingSuperAdminApproval = jpStatus === 'awaiting_super_admin_approval';
              const isArchivableStatus = archivableStatuses.includes(jpStatus);
              const canDelete = isAwaitingApproval || isRejected || isAwaitingSuperAdminApproval || jpStatus === 'rejected_by_super_admin';
              const isJobDescriptionClosed = jobProspect?.jobDescriptionClosedAt != null;
              const isSelected = selectedJobProspectIds.includes(jobProspect.id);

              return (
                <Tr key={jobProspect.id} style={isJobDescriptionClosed ? { backgroundColor: 'var(--color-bg-subtle)' } : {}}>
                  {showCheckboxes && (
                    <Td>
                      <Checkbox
                        checked={isSelected}
                        onChange={() => toggleJobProspectSelection(jobProspect.id)}
                      />
                    </Td>
                  )}
                  <Td>
                    {jobProspect.candidatePath && jobProspect.candidateName
                      ? <a href={jobProspect.candidatePath} target="_blank" rel="noopener noreferrer">{jobProspect.candidateName}</a>
                      : null
                    }
                  </Td>
                  <Td>
                    <a href={jobProspect.biopharmaGroupLink} target="_blank" rel="noopener noreferrer">{jobProspect.biopharmaGroupName}</a>
                  </Td>
                  <Td>
                    <a
                      href={jobProspect.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => handleJobProspectLinkClick(e, jobProspect)}
                    >
                      {jobProspect.title}
                    </a>
                  </Td>
                  <Td>
                    {jobProspect.biopharmaGroupId ? (
                      <Link href={`/biopharma_groups/${jobProspect.biopharmaGroupId}`} external size="sm">Board</Link>
                    ) : '—'}
                  </Td>
                  <Td>{jobProspect.addedByName}</Td>
                  <Td>{jobProspect?.candidateInterestLevel}</Td>
                  <Td>
                    <ActionRow align="end">
                      {isAwaitingApproval && (
                        <>
                          <Button onClick={() => handleApproveJobProspect(jobProspect)}>
                            Approve
                          </Button>
                          <Button onClick={() => handleRejectJobProspect(jobProspect)}>
                            Reject
                          </Button>
                        </>
                      )}
                      {isAwaitingSuperAdminApproval && (
                        <>
                          <Button onClick={() => handleSuperAdminApproveJobProspect(jobProspect)}>
                            Approve for Admin Review
                          </Button>
                          <Button onClick={() => handleSuperAdminRejectJobProspect(jobProspect)}>
                            Reject
                          </Button>
                        </>
                      )}
                      {isArchivableStatus && (
                        <Button onClick={() => handleArchiveJobProspect(jobProspect)}>
                          Archive
                        </Button>
                      )}
                      {canDelete && (
                        <Button
                          variant="destructive"
                          onClick={() => openDeleteJobProspectModal(jobProspect)}
                        >
                          Delete
                        </Button>
                      )}
                    </ActionRow>
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </Accordion>
    );
  };

  const jobProspectStatusGroups = state?.jobProspectStatusGroups || [];
  const hasJobProspects = jobProspectStatusGroups.some(
    group => group?.jobProspects && group.jobProspects.length > 0
  );

  if (!hasJobProspects) {
    return (
      <p style={{ textAlign: 'center', fontWeight: 'bold' }}>No active job prospects available</p>
    );
  }

  return (
    <>
      {jobProspectStatusGroups.map((jobProspectStatusGroup) =>
        renderStatusGroup(jobProspectStatusGroup)
      )}
    </>
  );
};

export default AllJobProspectsTable;
